import React, { useState, useEffect } from 'react';
import {
  Check,
  Trophy,
  Sprout,
  BookOpen,
  Target,
  Coffee,
  Flame,
  Pencil,
  CalendarDays,
  User,
  BarChart2,
  Calendar,
  Lightbulb,
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import i18n from '../i18n';
import { Button, FahryoLogo } from './UIComponents';
import * as Sentry from '@sentry/react';

export const OnboardingModal: React.FC<{
  isOpen: boolean;
  onFinish: (updates: any) => void;
  userName?: string;
}> = ({ isOpen, onFinish, userName }) => {
  const [step, setStep] = useState(1);
  const totalSteps = 5;
  // Track language as state so React re-renders when it changes
  const [selectedLang, setSelectedLang] = useState(i18n.language || 'de');

  // Wizard data
  const [experienceLevel, setExperienceLevel] = useState<
    'neuling' | 'fortgeschritten' | 'erfahren' | null
  >(null);
  const [displayName, setDisplayName] = useState(userName || '');
  const [examDate, setExamDate] = useState('');
  const [dailyGoal, setDailyGoal] = useState(20);
  const [leaderboardVisible, setLeaderboardVisible] = useState(true);
  const LANG_CACHE_KEY = 'fahryo_languages_cache';
  const [availableLanguages, setAvailableLanguages] = useState(() => {
    try {
      const cached = localStorage.getItem(LANG_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [
      { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
      { code: 'en', flag: '🇬🇧', label: 'English' },
      { code: 'tr', flag: '🇹🇷', label: 'Türkçe' },
      { code: 'uk', flag: '🇺🇦', label: 'Українська' },
      { code: 'ar', flag: '🇸🇦', label: 'العربية' },
      { code: 'ur', flag: '🇵🇰', label: 'اردو' },
      { code: 'ps', flag: '🇦🇫', label: 'پښتو' },
      { code: 'fa', flag: '🇮🇷', label: 'فارسی' },
      { code: 'hi', flag: '🇮🇳', label: 'हिन्दी' },
      { code: 'ro', flag: '🇷🇴', label: 'Română' },
      { code: 'pl', flag: '🇵🇱', label: 'Polski' },
      { code: 'ru', flag: '🇷🇺', label: 'Русский' },
      { code: 'bg', flag: '🇧🇬', label: 'Български' },
      { code: 'es', flag: '🇪🇸', label: 'Español' },
      { code: 'pt', flag: '🇵🇹', label: 'Português' },
      { code: 'prs', flag: '🇦🇫', label: 'دری' },
    ];
  });

  useEffect(() => {
    supabase
      .from('app_languages')
      .select('code, label, flag')
      .eq('enabled', true)
      .order('sort_order')
      .then(({ data }) => {
        if (data && data.length > 0) {
          setAvailableLanguages(data);
          try {
            localStorage.setItem(LANG_CACHE_KEY, JSON.stringify(data));
          } catch {}
        }
      });
  }, []);

  const [remountKey, setRemountKey] = useState(0);

  const handleLanguageSelect = async (code: string) => {
    await i18n.changeLanguage(code);
    setSelectedLang(code);
    // Trigger remount AFTER i18n has fully switched
    setRemountKey((k) => k + 1);
  };

  if (!isOpen) return null;

  // Use i18n.t() directly to guarantee fresh translations
  const T = (key: string) => i18n.t(key);

  const handleFinish = () => {
    onFinish({
      experienceLevel,
      displayName: displayName.trim() || undefined,
      examDate: examDate || null,
      dailyGoal,
      language: selectedLang,
      leaderboardVisible,
      xp: 50, // Welcome bonus
    });
  };

  const canProceed = () => {
    if (step === 2) return experienceLevel !== null;
    return true;
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
    else handleFinish();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-500"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      {/* Subtle decorative blobs */}
      <div className="absolute top-20 left-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-56 h-56 bg-blue-500/5 rounded-full blur-3xl" />

      {/* remountKey forces React to fully remount when language changes (after i18n has switched) */}
      <div
        key={remountKey}
        className="glass-strong rounded-3xl max-w-sm w-full p-6 relative overflow-hidden animate-in zoom-in-95 duration-500"
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-[var(--f-surface-elevated)]">
          <div
            className="h-full bg-gradient-to-r from-primary to-[var(--f-brand-gradient-end)] transition-all duration-500 ease-out rounded-r-full"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
        <div className="text-[10px] text-gray-400 text-right mt-2 mb-2">
          {step}/{totalSteps}
        </div>

        {/* Step 1: Language (FIRST!) */}
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <FahryoLogo tile />
              </div>
              <h2 className="text-2xl font-black text-[var(--f-text)] mb-2">
                {T('onboarding_language_title')}
              </h2>
              <p className="text-sm text-[var(--f-text-muted)]">{T('onboarding_language_desc')}</p>
            </div>
            <div className="max-h-[40vh] overflow-y-auto -mx-1 px-1">
              <div className="grid grid-cols-2 gap-3">
                {availableLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageSelect(lang.code)}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      selectedLang === lang.code
                        ? 'border-primary bg-red-50/50 dark:bg-red-900/10 shadow-sm'
                        : 'border-[var(--f-border)]'
                    }`}
                  >
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="font-bold text-sm text-[var(--f-text)]">{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Experience Level */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-[var(--f-text)] mb-2">
                {T('onboarding_experience_title')}
              </h2>
              <p className="text-sm text-[var(--f-text-muted)]">
                {T('onboarding_experience_desc')}
              </p>
            </div>
            <div className="space-y-3">
              {[
                {
                  id: 'neuling' as const,
                  Icon: Sprout,
                  iconColor: 'text-green-500',
                  label: T('onboarding_beginner'),
                  desc: T('onboarding_beginner_desc'),
                },
                {
                  id: 'fortgeschritten' as const,
                  Icon: BookOpen,
                  iconColor: 'text-blue-500',
                  label: T('onboarding_intermediate'),
                  desc: T('onboarding_intermediate_desc'),
                },
                {
                  id: 'erfahren' as const,
                  Icon: Target,
                  iconColor: 'text-primary',
                  label: T('onboarding_advanced'),
                  desc: T('onboarding_advanced_desc'),
                },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setExperienceLevel(opt.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
                    experienceLevel === opt.id
                      ? 'border-primary bg-red-50/50 dark:bg-red-900/10 shadow-md scale-[1.02]'
                      : 'border-[var(--f-border)] hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <opt.Icon size={28} className={opt.iconColor} />
                  <div>
                    <div className="font-bold text-[var(--f-text)]">{opt.label}</div>
                    <div className="text-xs text-[var(--f-text-muted)]">{opt.desc}</div>
                  </div>
                  {experienceLevel === opt.id && (
                    <div className="ml-auto">
                      <Check size={20} className="text-primary" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Nickname + Leaderboard visibility */}
        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <Pencil size={36} className="text-[var(--f-text-muted)]" />
              </div>
              <h2 className="text-2xl font-black text-[var(--f-text)] mb-2">
                {T('onboarding_name_title')}
              </h2>
              <p className="text-sm text-[var(--f-text-muted)]">{T('onboarding_name_desc')}</p>
            </div>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={T('onboarding_name_placeholder')}
              autoFocus
              className="w-full bg-[var(--f-surface-input)] border-2 border-[var(--f-border)] rounded-2xl px-5 py-4 text-lg text-center outline-none focus:border-primary text-[var(--f-text)] transition-colors"
              onKeyDown={(e) => e.key === 'Enter' && handleNext()}
            />
            <p className="text-[10px] text-gray-400 text-center mt-2">
              {T('onboarding_name_hint')}
            </p>

            {/* Leaderboard visibility opt-in (default: yes) */}
            <div className="mt-5 p-4 rounded-2xl border-2 border-[var(--f-border)] bg-[var(--f-surface-input)]">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 text-amber-500">
                  <Trophy size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-bold text-sm text-[var(--f-text)]">
                      {T('onboarding_leaderboard_title')}
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={leaderboardVisible}
                      onClick={() => setLeaderboardVisible((v) => !v)}
                      className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${
                        leaderboardVisible ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-transform shadow ${
                          leaderboardVisible ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-[11px] text-[var(--f-text-muted)] mt-1 leading-snug">
                    {T('onboarding_leaderboard_desc')}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-2 leading-snug flex items-center gap-1">
                    <Lightbulb size={11} /> {T('onboarding_leaderboard_rename_hint')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Exam Date + Daily Goal */}
        {step === 4 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-3">
                <CalendarDays size={36} className="text-[var(--f-text-muted)]" />
              </div>
              <h2 className="text-2xl font-black text-[var(--f-text)] mb-2">
                {T('onboarding_exam_title')}
              </h2>
              <p className="text-sm text-[var(--f-text-muted)]">{T('onboarding_exam_desc')}</p>
            </div>

            <div className="mb-5">
              <label className="text-xs text-[var(--f-text-muted)] font-bold uppercase tracking-wide mb-2 block">
                {T('onboarding_exam_date_label')}
              </label>
              <input
                type="date"
                value={examDate}
                onChange={(e) => setExamDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full bg-[var(--f-surface-input)] border-2 border-[var(--f-border)] rounded-xl px-4 py-3 outline-none focus:border-primary text-[var(--f-text)] transition-colors"
              />
            </div>

            <div>
              <label className="text-xs text-[var(--f-text-muted)] font-bold uppercase tracking-wide mb-3 block">
                {T('onboarding_daily_goal_label')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    val: 10,
                    label: T('onboarding_goal_easy'),
                    Icon: Coffee,
                    iconColor: 'text-amber-500',
                    desc: '~5 Min',
                  },
                  {
                    val: 20,
                    label: T('onboarding_goal_normal'),
                    Icon: BookOpen,
                    iconColor: 'text-blue-500',
                    desc: '~10 Min',
                  },
                  {
                    val: 30,
                    label: T('onboarding_goal_intense'),
                    Icon: Flame,
                    iconColor: 'text-primary',
                    desc: '~15 Min',
                  },
                ].map((opt) => (
                  <button
                    key={opt.val}
                    onClick={() => setDailyGoal(opt.val)}
                    className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                      dailyGoal === opt.val
                        ? 'border-primary bg-red-50/50 dark:bg-red-900/10 shadow-sm'
                        : 'border-[var(--f-border)]'
                    }`}
                  >
                    <opt.Icon size={22} className={`mb-1 ${opt.iconColor}`} />
                    <div className="text-xs font-bold text-[var(--f-text)]">{opt.label}</div>
                    <div className="text-[10px] text-gray-400">
                      {opt.val} {T('Fragen')}
                    </div>
                    <div className="text-[9px] text-gray-400">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Ready! */}
        {step === 5 && (
          <div className="animate-in fade-in zoom-in-95 duration-500 text-center">
            <div className="flex justify-center mb-4">
              <FahryoLogo tile />
            </div>
            <h2 className="text-2xl font-black text-[var(--f-text)] mb-2">
              {T('onboarding_ready_title')}
            </h2>
            <p className="text-sm text-[var(--f-text-muted)] mb-6">{T('onboarding_ready_desc')}</p>

            <div className="bg-[var(--f-surface-input)] rounded-2xl p-4 mb-6 space-y-2 text-left">
              {displayName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <User size={12} /> {T('onboarding_summary_name')}
                  </span>
                  <span className="font-bold text-[var(--f-text)]">{displayName}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-1">
                  <BarChart2 size={12} /> {T('onboarding_summary_experience')}
                </span>
                <span className="font-bold text-[var(--f-text)]">
                  {experienceLevel === 'neuling'
                    ? T('onboarding_beginner')
                    : experienceLevel === 'fortgeschritten'
                      ? T('onboarding_intermediate')
                      : T('onboarding_advanced')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 flex items-center gap-1">
                  <BookOpen size={12} /> {T('onboarding_summary_goal')}
                </span>
                <span className="font-bold text-[var(--f-text)]">
                  {dailyGoal} {T('Fragen')}
                </span>
              </div>
              {examDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Calendar size={12} /> {T('onboarding_summary_exam')}
                  </span>
                  <span className="font-bold text-[var(--f-text)]">
                    {new Date(examDate).toLocaleDateString(
                      selectedLang === 'de' ? 'de-DE' : 'en-US'
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-4 mb-4 border border-green-200 dark:border-green-800">
              <div className="text-lg font-black text-green-600">
                {T('onboarding_welcome_bonus')}
              </div>
              <p className="text-xs text-green-500 mt-1">{T('onboarding_welcome_bonus_desc')}</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6 space-y-2">
          <Button fullWidth onClick={handleNext} disabled={!canProceed()}>
            {step === totalSteps ? T('onboarding_start_button') : T('Weiter')}
          </Button>

          {step > 1 && step < totalSteps && (
            <button
              onClick={() => setStep(step - 1)}
              className="w-full text-center text-xs text-gray-400 hover:text-[var(--f-text-secondary)] py-2"
            >
              ← {T('Zurück')}
            </button>
          )}
          {step === 1 && (
            <button
              onClick={() => {
                Sentry.addBreadcrumb({
                  category: 'onboarding',
                  message: 'User skipped onboarding step 1',
                  level: 'info',
                  data: { step: 1, language: selectedLang },
                });
                Sentry.setTag('onboarding.skipped', 'true');
                onFinish({});
              }}
              className="w-full text-center text-xs text-gray-400 hover:text-[var(--f-text-secondary)] py-2"
            >
              {T('Überspringen')}
            </button>
          )}
          {step === totalSteps && (
            <div className="text-[9px] text-gray-400 text-center">
              {T('onboarding_terms_notice')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
